/**
 * Holding the caret in place while Formly rebuilds a route.
 *
 * Sorting the route replaces the rows: the array rebuild swaps the row element, and the field
 * components inside it then swap their own inputs on a later change-detection cycle. Whatever
 * held the caret is detached somewhere in that sequence and focus falls back to <body>.
 *
 * There is no event that announces the last pass, which is why waiting a fixed number of ticks
 * or animation frames does not work: the right number depends on the machine, the route length
 * and what else the page is doing. Every pass IS a DOM mutation, though, so a MutationObserver
 * turns "wait long enough" into "react to each change" — the final pass is the last one observed,
 * whatever it took to get there.
 */

/** A focus target described positionally, so it survives a rebuild that an element reference does not. */
export interface FocusIntent {
  /** Index of the stop the caret belongs to. */
  rowIndex: number;
  /** Which focusable field within that stop. */
  fieldIndex: number;
  /** How many focusable fields that stop had — a row showing fewer is still being rebuilt. */
  fieldCount: number;
}

/** The fields a planner can put a caret in. Buttons are excluded so the insert and delete
 *  controls cannot shift the numbering between describing a target and restoring it. */
export function focusablesIn(row: HTMLElement): HTMLElement[] {
  return Array.from(
    row.querySelectorAll<HTMLElement>('input:not([disabled]), textarea:not([disabled]), select:not([disabled])')
  );
}

/**
 * The field the caret belongs on, or undefined while that row is still being rebuilt.
 *
 * The count is what makes "is this row finished" answerable at all. A half-built row exposes
 * fewer inputs than the stop really has, and index N then lands on a different field than the
 * planner clicked — the arrival time instead of the location, say.
 */
export function intendedField(rows: HTMLElement[], intent: FocusIntent): HTMLElement | undefined {
  const row = rows[intent.rowIndex];
  if (!row) {
    return undefined;
  }
  const fields = focusablesIn(row);
  return fields.length === intent.fieldCount ? fields[intent.fieldIndex] : undefined;
}

/**
 * Keep the caret on whatever `resolve` points at, for as long as the DOM under `root` keeps
 * changing. Returns its own teardown.
 *
 * Gives up the moment focus lands anywhere else: that is the planner clicking on, and their
 * click outranks this. Callers must also call the teardown when the component is destroyed,
 * or the observer outlives the tree it watches.
 */
export function keepFocusOn(
  root: HTMLElement,
  resolve: () => HTMLElement | undefined,
  onEvent?: (event: 'applied' | 'released', detail?: unknown) => void
): () => void {
  const apply = (): void => {
    const field = resolve();
    // Only when it actually drifted. Not for the caret's sake — focus() on the element that
    // already holds it is a no-op per spec — but so the 'applied' signal means one real move
    // rather than one entry per mutation.
    if (field && root.ownerDocument.activeElement !== field) {
      field.focus({ preventScroll: true });
      onEvent?.('applied', { landed: root.ownerDocument.activeElement === field });
    }
  };

  // Our own focus() call fires focusin too, with the intended field as its target. That one is
  // not the planner moving on, which is why this compares against the field rather than just
  // counting events.
  const release = (event: Event): void => {
    if (event.target !== resolve()) {
      onEvent?.('released', (event.target as HTMLElement | null)?.tagName);
      stop();
    }
  };

  const observer = new MutationObserver(apply);
  const stop = (): void => {
    observer.disconnect();
    root.ownerDocument.removeEventListener('focusin', release, true);
    root.ownerDocument.removeEventListener('pointerdown', release, true);
  };

  root.ownerDocument.addEventListener('focusin', release, true);
  // focusin on its own does not catch every way of leaving. Clicking plain non-focusable space
  // drops the caret on <body> and fires nothing, and Safari and Firefox do not focus a button
  // when it is clicked — so deleting a stop there would mutate the route with the keeper still
  // armed, and it would pull the caret into whichever stop had shifted into that position. A
  // pointer going down elsewhere is the same intent, and it arrives before the click acts.
  root.ownerDocument.addEventListener('pointerdown', release, true);
  observer.observe(root, { childList: true, subtree: true });
  // The row may already be final — a short route can rebuild within the same task — and then
  // there is no mutation left to observe.
  apply();

  return stop;
}
